import { useEffect, useState } from "react";
import api from "../lib/api";
import { Calendar, Clock, FlaskConical, Loader2, Sparkles, Trash2, Save, Target, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function tryParse(raw) {
  if (!raw) return null;
  let txt = raw.trim();
  if (txt.startsWith("```")) txt = txt.replace(/^```(json)?/i, "").replace(/```$/, "").trim();
  try { return JSON.parse(txt); } catch {
    const m = txt.match(/\{[\s\S]*\}/);
    if (m) { try { return JSON.parse(m[0]); } catch {} }
    return null;
  }
}

const TABS = [
  { id: "plan", label: "Plan optimal", icon: Calendar },
  { id: "ab", label: "A/B Tests", icon: FlaskConical },
  { id: "saved", label: "Sauvegardés", icon: Save },
];

export default function PostingPlanner() {
  const [tab, setTab] = useState("plan");
  return (
    <div className="space-y-7 animate-fadeUp" data-testid="posting-planner-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// AI POSTING PLANNER</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Planification IA</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">Plan optimal de publication, créneaux high-engagement, A/B tests structurés et auto-optimisation.</p>
      </header>

      <div className="border-b border-[#1E1E24] flex gap-0 overflow-x-auto">
        {TABS.map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pp-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-wider transition border-b-2 whitespace-nowrap ${
                tab === t.id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}>
              <I className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "plan" && <PlanGenerator />}
      {tab === "ab" && <ABLab />}
      {tab === "saved" && <SavedPlans />}
    </div>
  );
}

function PlanGenerator() {
  const [form, setForm] = useState({ platform: "instagram", niche: "", audience_timezone: "Europe/Paris", posts_per_week: 5 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setLoading(true); setData(null);
    try {
      const r = await api.aiPostingPlan(form);
      const parsed = tryParse(r.raw);
      if (!parsed) { toast.error("Réponse IA invalide"); return; }
      setData(parsed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.createPostingPlan({ name: `${form.platform} · ${form.niche || "général"} · ${new Date().toLocaleDateString("fr-FR")}`, platform: form.platform, plan_data: data });
      toast.success("Plan sauvegardé");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Plateforme</label>
            <select data-testid="pp-platform" value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
              {["instagram", "tiktok", "youtube", "x", "linkedin"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Niche</label>
            <input data-testid="pp-niche" placeholder="fitness, finance..." value={form.niche} onChange={e => setForm({ ...form, niche: e.target.value })}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Fuseau horaire</label>
            <input data-testid="pp-tz" value={form.audience_timezone} onChange={e => setForm({ ...form, audience_timezone: e.target.value })}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Posts / semaine</label>
            <input data-testid="pp-freq" type="number" min="1" max="21" value={form.posts_per_week} onChange={e => setForm({ ...form, posts_per_week: parseInt(e.target.value) || 5 })}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
        </div>
        <button data-testid="pp-generate" onClick={generate} disabled={loading}
          className="mt-4 bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center gap-2 text-sm glow-volt">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Génération..." : "Générer le plan"}
        </button>
      </div>

      {data && (
        <div className="space-y-5" data-testid="pp-result">
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2"><Clock className="w-3 h-3" /> // CRÉNEAUX OPTIMAUX</div>
              <button data-testid="pp-save" onClick={save} disabled={saving}
                className="text-[#CCFF00] text-xs font-mono uppercase tracking-wider hover:text-white transition flex items-center gap-1">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Sauvegarder
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(data.best_slots || []).map((s, i) => (
                <div key={i} className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.04] p-4 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-lg text-white">{s.day}</div>
                    <div className="font-mono text-xl text-[#CCFF00]">{s.time}</div>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00] mb-1">{s.format}</div>
                  <div className="text-xs text-zinc-400 leading-relaxed">{s.reason}</div>
                </div>
              ))}
            </div>
          </section>

          {data.avoid_slots?.length > 0 && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-3 h-3" /> // À ÉVITER
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {data.avoid_slots.map((s, i) => (
                  <div key={i} className="border border-red-500/20 bg-red-500/5 p-3 rounded-sm">
                    <div className="text-sm text-white">{s.day} · {s.time}</div>
                    <div className="text-xs text-zinc-500 mt-1">{s.reason}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.weekly_mix && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">// MIX HEBDO RECOMMANDÉ</div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {Object.entries(data.weekly_mix).map(([k, v]) => (
                  <div key={k} className="border border-[#1E1E24] bg-[#0A0A0C] p-3 rounded-sm text-center">
                    <div className="font-mono text-2xl text-[#CCFF00]">{v}</div>
                    <div className="text-[10px] font-mono uppercase text-zinc-500 mt-1">{k}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.key_insights?.length > 0 && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">// INSIGHTS</div>
              <ul className="space-y-2">
                {data.key_insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 border-l-2 border-[#CCFF00]/30 pl-3 py-1">
                    <Target className="w-3.5 h-3.5 text-[#CCFF00] mt-1 shrink-0" />{insight}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.ab_tests?.length > 0 && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                <FlaskConical className="w-3 h-3" /> // A/B TESTS PROPOSÉS
              </div>
              <div className="space-y-2">
                {data.ab_tests.map((t, i) => (
                  <div key={i} className="border border-[#1E1E24] bg-[#0A0A0C] p-4 rounded-sm">
                    <div className="text-white font-medium mb-2">{t.hypothesis}</div>
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div className="border border-[#1E1E24] p-2 rounded-sm"><div className="text-[10px] font-mono text-[#CCFF00]">VARIANT A</div><div className="text-sm text-zinc-300 mt-1">{t.variant_a}</div></div>
                      <div className="border border-[#1E1E24] p-2 rounded-sm"><div className="text-[10px] font-mono text-[#00D4FF]">VARIANT B</div><div className="text-sm text-zinc-300 mt-1">{t.variant_b}</div></div>
                    </div>
                    <div className="text-xs font-mono text-zinc-500">métrique: {t.metric} · durée: {t.duration_days}j</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function ABLab() {
  const [hook, setHook] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = async () => {
    if (!hook) { toast.error("Saisis un hook"); return; }
    setLoading(true); setData(null);
    try {
      const r = await api.aiABVariants({ base_hook: hook, platform, count });
      const p = tryParse(r.raw);
      if (!p) { toast.error("Réponse IA invalide"); return; }
      setData(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 space-y-3">
        <textarea data-testid="ab-hook" rows={2} placeholder="Ton hook de base..." value={hook} onChange={e => setHook(e.target.value)}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="ab-platform" value={platform} onChange={e => setPlatform(e.target.value)}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            {["instagram", "tiktok", "youtube", "x", "linkedin"].map(p => <option key={p}>{p}</option>)}
          </select>
          <input data-testid="ab-count" type="number" min="2" max="6" value={count} onChange={e => setCount(parseInt(e.target.value) || 3)}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white w-20" />
          <button data-testid="ab-generate" onClick={generate} disabled={loading}
            className="bg-[#CCFF00] text-black font-medium px-5 py-2 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center gap-2 text-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            Générer variantes
          </button>
        </div>
      </div>

      {data && (
        <div className="space-y-4" data-testid="ab-result">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data.variants || []).map((v, i) => {
              const isWin = v.label === data.recommended_winner;
              return (
                <div key={i} className={`border p-4 rounded-sm ${isWin ? "border-[#CCFF00]/50 bg-[#CCFF00]/[0.04]" : "border-[#1E1E24] bg-[#0A0A0C]"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`font-mono text-2xl ${isWin ? "text-[#CCFF00]" : "text-white"}`}>VARIANT {v.label}</div>
                    {isWin && <span className="text-[9px] font-mono uppercase px-2 py-0.5 bg-[#CCFF00] text-black rounded-sm">WINNER</span>}
                  </div>
                  <div className="text-zinc-200 text-sm mb-3">{v.hook}</div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono text-zinc-500">angle: {v.angle}</span>
                    <span className="font-mono text-[#CCFF00]">{v.predicted_score}/100</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-l-2 border-[#CCFF00] bg-[#CCFF00]/[0.03] p-4 rounded-sm text-sm text-zinc-300">
            <span className="text-[#CCFF00] font-medium">Métrique à tracker :</span> {data.metric_to_track} · sample minimum : {data.sample_size_min} vues
          </div>
        </div>
      )}
    </div>
  );
}

function SavedPlans() {
  const [plans, setPlans] = useState([]);
  const refresh = async () => { try { setPlans(await api.listPostingPlans()); } catch { toast.error("Erreur"); } };
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.deletePostingPlan(id); refresh();
  };

  if (plans.length === 0) {
    return <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center text-zinc-500 text-sm">Aucun plan sauvegardé.</div>;
  }
  return (
    <div className="space-y-3">
      {plans.map((p, i) => (
        <div key={p.id} data-testid={`saved-plan-${i}`} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-medium">{p.name}</div>
            <div className="text-[11px] font-mono text-zinc-500 mt-1">
              {p.platform} · {(p.plan_data?.best_slots || []).length} créneaux · {new Date(p.created_at).toLocaleDateString("fr-FR")}
            </div>
          </div>
          <button onClick={() => remove(p.id)} className="text-zinc-600 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
